import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  /** `YYYY-MM-DD`, or '' for none. */
  value: string;
  onChange: (value: string) => void;
  id?: string;
  className?: string;
}

interface Parts { day: string; month: string; year: string }

function split(value: string): Parts {
  const [year = '', month = '', day = ''] = value ? value.split('-') : [];
  return { year, month: month ? String(Number(month)) : '', day: day ? String(Number(day)) : '' };
}

function daysIn(year: number, month: number): number {
  return new Date(Date.UTC(year || 2000, month, 0)).getUTCDate();
}

/**
 * A date of birth is picked from three selects, not a calendar: nobody pages
 * back forty years month by month. Emits a full `YYYY-MM-DD` only once the
 * three parts are there, and '' when any is cleared.
 */
export default function BirthDateInput({ value, onChange, id, className = '' }: Props) {
  const { t, i18n } = useTranslation();
  const [parts, setParts] = useState<Parts>(() => split(value));

  useEffect(() => { if (value) setParts(split(value)); }, [value]);

  const months = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(i18n.language, { month: 'long', timeZone: 'UTC' });
    return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(Date.UTC(2000, i, 1))));
  }, [i18n.language]);

  const thisYear = new Date().getFullYear();
  const years = useMemo(() => Array.from({ length: 110 }, (_, i) => thisYear - i), [thisYear]);
  const maxDay = daysIn(Number(parts.year), Number(parts.month) || 1);

  function update(next: Partial<Parts>) {
    const merged = { ...parts, ...next };
    if (merged.day && Number(merged.day) > daysIn(Number(merged.year), Number(merged.month) || 1)) merged.day = '';
    setParts(merged);
    const complete = merged.day && merged.month && merged.year;
    onChange(complete
      ? `${merged.year}-${merged.month.padStart(2, '0')}-${merged.day.padStart(2, '0')}`
      : '');
  }

  const select = 'input !px-2 cursor-pointer';
  return (
    <div className={`grid grid-cols-[4.5rem_1fr_5.5rem] gap-2 ${className}`}>
      <select id={id} className={select} value={parts.day} aria-label={t('birthDate.day')}
        onChange={e => update({ day: e.target.value })}>
        <option value="">{t('birthDate.day')}</option>
        {Array.from({ length: maxDay }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
      </select>
      <select className={select} value={parts.month} aria-label={t('birthDate.month')}
        onChange={e => update({ month: e.target.value })}>
        <option value="">{t('birthDate.month')}</option>
        {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
      <select className={select} value={parts.year} aria-label={t('birthDate.year')}
        onChange={e => update({ year: e.target.value })}>
        <option value="">{t('birthDate.year')}</option>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}
